import { Controller } from "@hotwired/stimulus"
import { ShowHide } from "./concerns/showHide"
import { usePwaHelpers } from "./concerns/use_pwa_helpers"

export default class extends Controller {
  static targets = [
    "platformLabel",
    "statusContainer",
    "installPromptState",
    "manualState",
    "unsupportedState",
    "installedState",
    "instructionsColumn",
    "videoColumn",
    "iosPre26Instructions",
    "ios26OnwardInstructions",
    "androidInstructions",
    "appleDesktopInstructions",
    "desktopChromeInstructions",
    "iosVideo",
    "androidVideo"
  ]

  static values = {
    iosLabel: String,
    androidLabel: String,
    appleDesktopLabel: String,
    desktopChromeLabel: String,
    installableLabel: String,
    unsupportedLabel: String
  }

  initialize() {
    ShowHide(this, {hiddenClassName: "d-none"})
    usePwaHelpers(this)
  }

  connect() {
    this.installStateUnsubscribe = this.onInstallStateChange(() => this.render())
    this.render()
  }

  disconnect() {
    this.installStateUnsubscribe?.()
  }

  async promptInstall(event) {
    event.preventDefault()

    await this.promptInstallApp()
    this.render()
  }

  render() {
    this.hideAll(this.stateTargets())
    this.hideAll(this.instructionTargets())
    this.hideAll(this.videoTargets())
    this.hide(this.statusContainerTarget)
    this.toggleVideoLayout(false)
    this.updatePlatformLabel()

    switch (this.getInstallState()) {
    case "installed":
      this.showStatus(this.installedStateTarget)
      break
    case "install_prompt_available":
      this.showStatus(this.installPromptStateTarget)
      if (this.getPlatform() !== "unsupported") {
        this.show(this.manualStateTarget)
        this.showPlatformInstructionsAndVideo()
      }
      break
    case "manual_only":
      this.show(this.manualStateTarget)
      this.showPlatformInstructionsAndVideo()
      break
    default:
      this.showStatus(this.unsupportedStateTarget)
    }
  }

  showStatus(stateTarget) {
    this.show(this.statusContainerTarget)
    this.show(stateTarget)
  }

  showPlatformInstructionsAndVideo() {
    let hasVideoForPlatform = false

    switch (this.getPlatform()) {
    case "ios_safari":
      if (this.isIos26Onward()) {
        this.show(this.ios26OnwardInstructionsTarget)
      } else {
        this.show(this.iosPre26InstructionsTarget)
      }
      if (this.hasIosVideoTarget) {
        this.show(this.iosVideoTarget)
        hasVideoForPlatform = true
      }
      break
    case "android_chrome":
      this.show(this.androidInstructionsTarget)
      if (this.hasAndroidVideoTarget) {
        this.show(this.androidVideoTarget)
        hasVideoForPlatform = true
      }
      break
    case "apple_desktop_safari":
      this.show(this.appleDesktopInstructionsTarget)
      break
    case "desktop_chrome":
      this.show(this.desktopChromeInstructionsTarget)
      break
    }

    this.toggleVideoLayout(hasVideoForPlatform)
  }

  toggleVideoLayout(videoVisible) {
    if (this.hasInstructionsColumnTarget) {
      this.instructionsColumnTarget.classList.toggle("col-xl-7", videoVisible)
      this.instructionsColumnTarget.classList.toggle("col-xl-12", !videoVisible)
    }

    if (this.hasVideoColumnTarget) {
      if (videoVisible) {
        this.show(this.videoColumnTarget)
      } else {
        this.hide(this.videoColumnTarget)
      }
    }
  }

  updatePlatformLabel() {
    if (!this.hasPlatformLabelTarget) {
      return
    }

    switch (this.getPlatform()) {
    case "ios_safari":
      this.platformLabelTarget.textContent = this.iosLabelValue
      break
    case "android_chrome":
      this.platformLabelTarget.textContent = this.androidLabelValue
      break
    case "apple_desktop_safari":
      this.platformLabelTarget.textContent = this.appleDesktopLabelValue
      break
    case "desktop_chrome":
      this.platformLabelTarget.textContent = this.desktopChromeLabelValue
      break
    default:
      this.platformLabelTarget.textContent = this.hasDeferredInstallPrompt() ? this.installableLabelValue : this.unsupportedLabelValue
    }
  }

  stateTargets() {
    return [
      this.installPromptStateTarget,
      this.manualStateTarget,
      this.unsupportedStateTarget,
      this.installedStateTarget
    ]
  }

  instructionTargets() {
    return [
      this.iosPre26InstructionsTarget,
      this.ios26OnwardInstructionsTarget,
      this.androidInstructionsTarget,
      this.appleDesktopInstructionsTarget,
      this.desktopChromeInstructionsTarget
    ]
  }

  isIos26Onward() {
    const iosMajorVersion = this.getIosMajorVersion()

    return iosMajorVersion !== null && iosMajorVersion >= 26
  }


  videoTargets() {
    return [
      ...(this.hasIosVideoTarget ? [this.iosVideoTarget] : []),
      ...(this.hasAndroidVideoTarget ? [this.androidVideoTarget] : [])
    ]
  }
}
